import React, { useState, useEffect, useCallback } from 'react';
import ProposalsList from '../../components/ProposalsList';
import ProposalForm from '../../components/ProposalForm';
import ProposalView from '../../components/ProposalView';
import { FileText, PlusCircle, LayoutGrid, Clock, CheckCircle2, AlertCircle, XCircle, Timer, Inbox, Trash2, X } from 'lucide-react';
import { proposalApi, Proposal, ProposalStatus } from '../../api/proposalApi';
import { getBranding, BrandingData } from '../../api/brandingApi';
import { toast } from 'react-toastify';

type TabType =
  | 'All'
  | 'Drafts'
  | 'Sent'
  | 'Accepted'
  | 'Declined'
  | 'Expired';

export default function ProposalModule() {
  const [proposals, setProposals] = useState<Proposal[]>([]);
  const [branding, setBranding] = useState<BrandingData | null>(null);
  const [loading, setLoading] = useState(true);
  const [editingProposal, setEditingProposal] = useState<Proposal | null>(null);
  const [previewProposalId, setPreviewProposalId] = useState<string | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [proposalToDelete, setProposalToDelete] = useState<Proposal | null>(null);
  const [activeTab, setActiveTab] = useState<TabType>('All');
  const [showForm, setShowForm] = useState(false);

  const tabs = [
    { id: 'All', label: 'All Proposals', icon: <LayoutGrid size={18} /> },
    // { id: 'Create', label: 'Create Proposal', icon: <PlusCircle size={18} /> },
    { id: 'Drafts', label: 'Drafts', icon: <Clock size={18} /> },
    { id: 'Sent', label: 'Sent', icon: <Timer size={18} /> },
    { id: 'Accepted', label: 'Accepted', icon: <CheckCircle2 size={18} /> },
    { id: 'Declined', label: 'Declined', icon: <XCircle size={18} /> },
    { id: 'Expired', label: 'Expired', icon: <AlertCircle size={18} /> },
  ];

  const fetchProposals = useCallback(async () => {
    setLoading(true);
    try {
      const data = await proposalApi.getProposals();
      setProposals(data);
    } catch (error) {
      setProposals([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProposals();
    const loadBranding = async () => {
      const data = await getBranding();
      setBranding(data);
    };
    loadBranding();
  }, [fetchProposals]);

  const handleProposalCreated = () => {
    setEditingProposal(null);
    fetchProposals();
    setShowForm(false);
  };

  const handleEdit = (proposal: Proposal) => {
    setEditingProposal(proposal);
    setShowForm(true);
  };

  const handleDelete = (id: string) => {
    const proposal = proposals.find(p => p.id === id);
    if (proposal) {
      setProposalToDelete(proposal);
      setDeleteModalOpen(true);
    }
  };

  const confirmDelete = async () => {
    if (!proposalToDelete) return;
    try {
      await proposalApi.deleteProposal(proposalToDelete.id);
      toast.success(`Proposal "${proposalToDelete.title}" deleted successfully!`);
      setDeleteModalOpen(false);
      fetchProposals();
    } catch (error) {
      // Error is already toasted by the API wrapper
    }
  };

  const handleSend = async (id: string) => {
    try {
      await proposalApi.sendProposal(id);
      toast.success("Proposal sent successfully");
      fetchProposals();
    } catch (error) {
      // Handled by API wrapper
    }
  };

  const handleView = (id: string) => {
    setPreviewProposalId(id);
  };

  const onTabChange = (tab: TabType) => {
    if (showForm) {
      setShowForm(false);
      setEditingProposal(null);
    }

    setActiveTab(tab);
  };


  const getFilteredProposals = (status?: ProposalStatus) => {
    if (status === undefined) return proposals;
    return proposals.filter(p => p.status === status);
  };

  return (
    <>
      <div className="min-h-screen pt-10 p-6 md:p-8 lg:p-10 md:ml-0">
        {/* Header */}
        <header className="mt-5 mb-8 flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2 leading-tight mb-0">
              <FileText className="text-strivre-burgundy" size={24} />
              Proposals
            </h1>
            <p className="text-slate-500 text-sm mt-1 text-left">
              Draft, track, and manage your professional business proposals.
            </p>
          </div>

          <button
            onClick={() => {
              setEditingProposal(null);
              setShowForm(true);
            }}
            className="btn-primary btn-md"
          >
            <PlusCircle size={18} className="mr-2" />
            Create Proposal
          </button>
        </header>

        {/* Tab Navigation */}
        <div className="w-full lg:w-auto overflow-visible">
          <div className="flex flex-wrap gap-1 p-1 md:p-1.5 bg-strivre-softgrey rounded-2xl md:rounded-[1.25rem] border border-strivre-lightnavy shadow-inner mb-6">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => onTabChange(tab.id as TabType)}
                className={`flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-xs md:text-sm font-bold transition-all flex-1 lg:flex-none ${
                  activeTab === tab.id
                    ? 'bg-strivre-burgundy text-white shadow-sm'
                    : 'text-slate-500 hover:text-strivre-burgundy'
                }`}
              >
                {tab.icon}
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        <div className="transition-all duration-300">
          {showForm ? (
            <ProposalForm 
              onProposalCreated={handleProposalCreated} 
              branding={branding}
              initialData={editingProposal}
            />
          ) : (
            loading ? (
              <div className="bg-white rounded-[2rem] border border-strivre-lightnavy p-20 flex justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-4 border-strivre-lightnavy border-t-strivre-burgundy"></div>
              </div>
            ) : getFilteredProposals(activeTab === 'All' ? undefined : (
              activeTab === 'Drafts' ? ProposalStatus.Draft :
              activeTab === 'Sent' ? ProposalStatus.Sent :
              activeTab === 'Accepted' ? ProposalStatus.Accepted :
              activeTab === 'Declined' ? ProposalStatus.Declined : ProposalStatus.Expired
            )).length > 0 ? (
              <ProposalsList 
                proposals={getFilteredProposals(activeTab === 'All' ? undefined : (
                  activeTab === 'Drafts' ? ProposalStatus.Draft :
                  activeTab === 'Sent' ? ProposalStatus.Sent :
                  activeTab === 'Accepted' ? ProposalStatus.Accepted :
                  activeTab === 'Declined' ? ProposalStatus.Declined : ProposalStatus.Expired
                ))} 
                loading={loading} 
                onSend={handleSend}
                onView={handleView}
                onEdit={handleEdit}
                onDelete={handleDelete}
              />
            ) : (
              <div className="bg-white rounded-[2rem] border border-strivre-lightnavy p-20 text-center animate-fade-in">
                <Inbox className="mx-auto text-strivre-lightnavy mb-4" size={48} />
                <p className="text-slate-400 font-bold uppercase tracking-widest text-sm">No proposals found</p>
              </div>
            )
          )}
        </div>

        {/* Quick Stats Footer */}
        {activeTab === 'All' && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
            <div className="bg-white p-6 rounded-[2rem] border border-slate-100 shadow-sm flex items-center gap-4">
              <div className="w-12 h-12 shrink-0 bg-strivre-lightburgundy rounded-2xl flex items-center justify-center text-strivre-burgundy font-bold">{proposals.length}</div>
              <div className="text-left">
                <p className="text-xs font-bold text-slate-400 uppercase tracking-widest">Total Proposals</p>
                <p className="text-xl font-bold text-slate-900">{proposals.length} Active</p>
              </div>
            </div>
            <div className="bg-white p-6 rounded-[2rem] border border-slate-100 shadow-sm flex items-center gap-4">
              <div className="w-12 h-12 shrink-0 bg-emerald-50 rounded-2xl flex items-center justify-center text-emerald-600 font-bold">
                {proposals.filter(p => p.status === ProposalStatus.Accepted).length}
              </div>
              <div className="text-left">
                <p className="text-xs font-bold text-slate-400 uppercase tracking-widest">Accepted</p>
                <p className="text-xl font-bold text-slate-900">
                  {proposals.filter(p => p.status === ProposalStatus.Accepted).length} Signed
                </p>
              </div>
            </div>
            <div className="bg-white p-6 rounded-[2rem] border border-slate-100 shadow-sm flex items-center gap-4">
              <div className="w-12 h-12 shrink-0 bg-amber-50 rounded-2xl flex items-center justify-center text-amber-600 font-bold">
                {proposals.filter(p => p.status === ProposalStatus.Expired).length}
              </div>
              <div className="text-left">
                <p className="text-xs font-bold text-slate-400 uppercase tracking-widest">Expired</p>
                <p className="text-xl font-bold text-slate-900">
                  {proposals.filter(p => p.status === ProposalStatus.Expired).length} Proposals
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      <ProposalView 
        isOpen={!!previewProposalId} 
        onClose={() => setPreviewProposalId(null)} 
        proposalId={previewProposalId || undefined}
        branding={branding}
      />

      {/* Delete Confirmation Modal */}
      {deleteModalOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm animate-fade-in" onClick={() => setDeleteModalOpen(false)} />
          <div className="relative w-full max-w-md bg-white rounded-[2.5rem] shadow-2xl p-8 animate-scale-in text-center overflow-hidden">
            <button onClick={() => setDeleteModalOpen(false)} className="absolute top-6 right-6 text-slate-400 hover:text-slate-600 transition-colors">
              <X size={24} />
            </button>

            <div className="w-20 h-20 bg-red-50 rounded-3xl flex items-center justify-center text-red-600 mx-auto mb-6">
              <Trash2 size={40} />
            </div>
            
            <h3 className="text-2xl font-bold text-slate-900 mb-2">Delete Proposal</h3>
            <p className="text-slate-500 font-medium mb-8 leading-relaxed px-4">
              Are you sure you want to delete <span className="font-medium text-slate-900">{proposalToDelete?.title}</span>? 
              This action is permanent and cannot be undone.
            </p>

            <div className="flex gap-3">
              <button 
                onClick={() => setDeleteModalOpen(false)}
                className="flex-1 py-4 bg-strivre-softgrey text-slate-600 rounded-2xl font-bold hover:bg-slate-200 transition-all active:scale-95"
              >
                Cancel
              </button>
              <button 
                onClick={confirmDelete}
                className="flex-1 py-4 bg-red-600 text-white rounded-2xl font-bold shadow-lg shadow-red-200 hover:bg-red-700 transition-all active:scale-95"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
